import { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Trash2, Image as ImageIcon, Video, FileText, HardDrive, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICONS = { image: ImageIcon, video: Video, document: FileText, other: HardDrive };

const MAX_IMAGE_MB = 100;
const MAX_VIDEO_MB = 500;
const MAX_DOC_MB = 100;

const ACCEPTED = "image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv";

function classify(mime) {
  if (!mime) return "other";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.includes("pdf") || mime.includes("word") || mime.startsWith("text/")) return "document";
  return "other";
}

function validate(file) {
  const kind = classify(file.type);
  const mb = file.size / 1024 / 1024;
  if (kind === "image" && mb > MAX_IMAGE_MB) return `Image is too large (max ${MAX_IMAGE_MB} MB)`;
  if (kind === "video" && mb > MAX_VIDEO_MB) return `Video is too large (max ${MAX_VIDEO_MB} MB)`;
  if (kind === "document" && mb > MAX_DOC_MB) return `Document is too large (max ${MAX_DOC_MB} MB)`;
  if (kind === "other") return `Unsupported file type: ${file.type || "unknown"}`;
  return null;
}

export default function Files() {
  const [files, setFiles] = useState([]);
  const [type, setType] = useState("all");
  const [source, setSource] = useState("all"); // all | upload | generated
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [info, setInfo] = useState(null);
  const fileRef = useRef(null);

  const token = useMemo(() => (typeof window !== "undefined" ? localStorage.getItem("alphacore_token") : null), []);
  const buildUrl = (f) => {
    // Prefer new object-storage download endpoint; fall back to legacy static URL if present.
    if (f?.url?.startsWith("/api/files/") && f.url.includes("/download")) {
      return `${BACKEND_URL}${f.url}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
    }
    return `${BACKEND_URL}${f.url}`;
  };

  const load = (t = type, s = source) => {
    const params = {};
    if (t && t !== "all") params.type = t;
    if (s && s !== "all") params.source = s;
    api.get("/files", { params }).then((r) => setFiles(r.data.items)).catch(() => {});
    api.get("/files/storage-info").then((r) => setInfo(r.data)).catch(() => {});
  };
  useEffect(() => { load(type, source); }, [type, source]);

  const filtered = q ? files.filter((f) => (f.name || "").toLowerCase().includes(q.toLowerCase())) : files;

  const handleUpload = async (f) => {
    if (!f) return;
    const err = validate(f);
    if (err) { toast.error(err); return; }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", "root");
      await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success(`Uploaded ${f.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onFileInput = (e) => handleUpload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files?.[0]);
  };

  const remove = async (id) => {
    await api.delete(`/files/${id}`);
    load();
    toast.success("File deleted");
  };

  const fmt = (n) => n < 1024 ? `${n} B` : n < 1024**2 ? `${(n/1024).toFixed(1)} KB` : n < 1024**3 ? `${(n/1024**2).toFixed(1)} MB` : `${(n/1024**3).toFixed(2)} GB`;

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline">[ MEDIA LIBRARY ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight">Media Library</h1>
          <p className="text-white/55 text-sm mt-1.5">Upload files or let AI agents fill your library — every image, video and document, securely stored in the cloud.</p>
        </div>
        <div className="flex items-center gap-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files…" className="bg-[#0A0A0E] border border-white/10 rounded-sm px-3 py-2 text-sm w-56" data-testid="files-search-input" />
          <input ref={fileRef} type="file" accept={ACCEPTED} className="hidden" onChange={onFileInput} data-testid="file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 rounded-full" data-testid="upload-file-btn">
            <Upload className="h-4 w-4 mr-2" /> {uploading ? `Uploading ${progress}%` : "Upload"}
          </Button>
        </div>
      </div>

      {/* Drop-zone + storage summary */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`mt-6 border border-dashed rounded-sm p-6 flex items-center justify-between gap-4 transition ${dragOver ? "bg-[#00E5FF]/5 border-[#00E5FF]" : "border-white/10 bg-[#050505]"}`}
        data-testid="files-dropzone"
      >
        <div className="flex items-center gap-3">
          <Upload className="h-5 w-5 text-[#00E5FF]" />
          <div>
            <div className="text-sm">{dragOver ? "Drop to upload" : "Drag & drop files here"}</div>
            <div className="text-xs text-white/40 mt-0.5">Images ≤ {MAX_IMAGE_MB} MB · Videos ≤ {MAX_VIDEO_MB} MB · Docs ≤ {MAX_DOC_MB} MB · 1 credit per upload</div>
          </div>
        </div>
        {info && (
          <div className="text-right text-xs text-white/60">
            <div>Total used: <span className="text-white">{fmt(info.total_bytes || 0)}</span></div>
            <div className="mt-0.5">
              {(info.by_type?.image?.count || 0)} images · {(info.by_type?.video?.count || 0)} videos · {(info.by_type?.document?.count || 0)} docs
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Tabs value={type} onValueChange={setType}>
          <TabsList className="bg-[#050505] border border-white/5 rounded-sm h-auto">
            {["all", "image", "video", "document", "other"].map((t) => (
              <TabsTrigger key={t} value={t} className="data-[state=active]:bg-white/10 data-[state=active]:text-white capitalize px-4 py-1.5" data-testid={`files-tab-${t}`}>{t}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Tabs value={source} onValueChange={setSource}>
          <TabsList className="bg-[#050505] border border-white/5 rounded-sm h-auto">
            {[["all", "All"], ["upload", "Uploads"], ["generated", "AI Generated"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} className="data-[state=active]:bg-white/10 data-[state=active]:text-white px-4 py-1.5" data-testid={`files-source-${v}`}>{l}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {filtered.map((f) => {
          const Icon = TYPE_ICONS[f.type] || HardDrive;
          const src = buildUrl(f);
          return (
            <div key={f.file_id} className="kr-card p-3 group relative" data-testid={`file-card-${f.file_id}`}>
              <div className="aspect-square bg-[#050505] border border-white/5 flex items-center justify-center overflow-hidden">
                {f.type === "image" ? (
                  <img src={src} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
                ) : f.type === "video" ? (
                  <video src={src} muted playsInline className="w-full h-full object-cover" />
                ) : (
                  <Icon className="h-10 w-10 text-white/40" />
                )}
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="text-xs truncate flex-1" title={f.name}>{f.name}</div>
                {f.source === "generated" && (
                  <span className="text-[10px] px-1.5 py-0.5 border border-[#00E5FF]/40 text-[#00E5FF] rounded-sm flex items-center gap-1"><Sparkles className="h-2.5 w-2.5" /> AI</span>
                )}
              </div>
              <div className="kr-overline mt-0.5">{f.type} · {fmt(f.size)}</div>
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 flex gap-1 transition">
                <a href={src} target="_blank" rel="noreferrer" download={f.name} className="p-1.5 bg-black/60 border border-white/10 hover:bg-white/10" data-testid={`download-file-${f.file_id}`}><Download className="h-3 w-3" /></a>
                <button onClick={() => remove(f.file_id)} className="p-1.5 bg-black/60 border border-white/10 text-red-300 hover:bg-red-500/20" data-testid={`delete-file-${f.file_id}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-20 text-white/40">
            <HardDrive className="h-10 w-10 mx-auto mb-3" />
            <div className="text-sm">{q ? `No files match "${q}"` : "No files yet. Upload your first asset or generate one with K-Visual."}</div>
          </div>
        )}
      </div>
    </div>
  );
}
